import { ErrorBoundary } from '@sentry/react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from '@/hooks/useTranslation';
import { BreadcrumbNav } from './components/BreadcrumbNav';
import { CompanyHeader } from './components/CompanyHeader';
import { ActionButtonGroup } from './components/ActionButtonGroup';
import { PriceAnalyticsCard } from './components/PriceAnalyticsCard';
import { PortfolioPositionCard } from './components/PortfolioPositionCard';
import { BlurredBackground } from './components/BlurredBackground';
import { DataSources } from './components/DataSources';
import { Bones } from './components/Bones';
import { useMetricData } from './hooks/useMetricData';

// For FloatingMenu so it can get the intersection point
export const HEADER_BOTTOM = 'header-bottom';

export const Header = () => {
  const data = useMetricData();
  const t = useTranslation();

  const updated =
    data.status === 'loading' ? (
      <Bones className="w-[20ch]" />
    ) : (
      <time dateTime={data.lastAnalysisUpdateFormatted}>
        {t('page.companyReport.header.metadata.updated')} {/* TODO: i18n */}
        {formatDistanceToNow(data.lastAnalysisUpdateFormatted)} ago
      </time>
    );

  return (
    <div className="relative z-1 [--bar-h:6rem] [-webkit-tap-highlight-color:transparent] lg:mb-x2">
      <div className="relative max-md:pb-x2 md:bg-[white]/[.01] lg:rounded-md lg:shadow-lg">
        <BlurredBackground />
        <div className="relative grid gap-x1_5 px-x2 pt-x2 md:grid-cols-[1.618fr_1fr] md:gap-x-x4 md:gap-y-x2 md:px-x4">
          <section className="grid gap-inherit">
            <BreadcrumbNav>
              <span className="md:hidden">{updated}</span>
            </BreadcrumbNav>
            <div>
              <CompanyHeader />
            </div>
            <div className="grid max-sm:gap-inherit">
              <div className="py-x0_5 md:pb-x0">
                <ErrorBoundary
                  fallback={
                    <div className="text-xs text-bad">
                      {/* TODO: i18n */}
                      Something went wrong, please refresh the page.
                    </div>
                  }
                >
                  <ActionButtonGroup />
                </ErrorBoundary>
              </div>
              <div className="hidden min-h-[--bar-h] flex-wrap content-center items-center gap-x2 md:flex md:gap-y-0">
                <div className="whitespace-nowrap">
                  <DataSources />
                </div>
                <div className="whitespace-nowrap text-xs text-softer">
                  {updated}
                </div>
              </div>
            </div>
          </section>
          <div className="grid auto-rows-[auto_var(--bar-h)] gap-x0_75 md:gap-x2">
            <section className="relative min-h-[--bar-h]">
              <PriceAnalyticsCard />
            </section>
            <section className="h-full">
              <PortfolioPositionCard />
            </section>
          </div>
          <div className="md:hidden">
            <DataSources />
          </div>
        </div>
      </div>

      <span id={HEADER_BOTTOM} />
    </div>
  );
};
